import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import brush from '../../sources/Graph/Brush_purple.png';
import FeatureOne from '../../sources/Graph/Feature_01.png';
import FeatureTwo from '../../sources/Graph/Feature_02.png';
import MindverseLogo from '../../sources/Logo/Mindverse Logo.png';
import EnspirenLogo from '../../sources/Logo/Enspiren Logo.png';
import LevavLogo from '../../sources/Logo/LEVAV Logo.png';
import AppStoreLogo from '../../sources/Icon/AppStore.png';
import GooglePlayLogo from '../../sources/Icon/GooglePlay.png';

export default function ServiceFlow() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const textStyle = {
        fontFamily: "Noto Sans TC",
        color: "#4E433C",
        fontSize: { xs: 16, md: 18 },
        fontWeight: 400,
        lineHeight: "180%",
        textAlign: "justify",
        letterSpacing: "0.5px"
    };

    const listItemStyle = {
        ...textStyle,
        marginBottom: '12px'
    };

    return (
        <Box width="100%" sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'transparent' }}>
            <Typography
                fontFamily={"Noto Sans TC"}
                textAlign={"center"}
                fontSize={{ xs: "28px", md: "42px" }}
                fontWeight={700}
                color='#7DC4A2'
                mb={6}
                sx={{
                    textShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    lineHeight: '100%',
                    position: 'relative',
                    backgroundImage: `url(${brush})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundPositionY: { xs: 25, md: 45 }
                }}
            >
                {t('ServiceFlow.title')}
            </Typography>

            <Box sx={{ maxWidth: '1200px', margin: '0 auto', px: { xs: 2, md: 4 } }}>
                {/* Integrated feature content */}
                <Box component="section" aria-label="Features" width="100%" sx={{ my: { xs: 5, md: 8 } }}>
                    <Box
                        sx={{
                            width: '100%',
                            px: { xs: 1, md: 2 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 4, md: 6 },
                        }}
                    >
                        <Box
                            role="group"
                            aria-label="Feature visuals"
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: { xs: 3, md: 5 },
                                alignItems: 'start',
                            }}
                        >
                            <Box
                                component="img"
                                src={FeatureTwo}
                                alt="Feature 02"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                    borderRadius: { xs: 2, md: 3 },
                                }}
                            />
                            <Box
                                component="img"
                                src={FeatureOne}
                                alt="Feature 01"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                    borderRadius: { xs: 2, md: 3 },
                                }}
                            />
                        </Box>

                        <Box
                            role="group"
                            aria-label="Partner logos"
                            sx={{
                                width: '100%',
                                maxWidth: '960px',
                                margin: '0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 3, md: 5 },
                            }}
                        >
                            <Box
                                role="group"
                                aria-label="Logo row"
                                sx={{
                                    width: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                    gap: { xs: 3, md: 5 },
                                    alignItems: 'center',
                                }}
                            >
                                <Link
                                    href="https://mindverse.cc/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                                >
                                    <Box sx={{ height: { xs: '90px', md: '120px' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box
                                            component="img"
                                            src={MindverseLogo}
                                            alt="Mindverse Logo"
                                            sx={{ width: '100%', maxWidth: '220px', height: 'auto', objectFit: 'contain' }}
                                        />
                                    </Box>
                                </Link>

                                <Link
                                    href="https://www.enspiren.hk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                                >
                                    <Box sx={{ height: { xs: '90px', md: '120px' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box
                                            component="img"
                                            src={EnspirenLogo}
                                            alt="Enspiren Logo"
                                            sx={{ width: { xs: '155px', md: '185px' }, height: { xs: '155px', md: '185px' }, objectFit: 'contain' }}
                                        />
                                    </Box>
                                </Link>

                                <Box sx={{ height: { xs: '90px', md: '120px' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box
                                        component="img"
                                        src={LevavLogo}
                                        alt="LEVAV Logo"
                                        sx={{ width: '100%', maxWidth: '180px', height: 'auto', objectFit: 'contain' }}
                                    />
                                </Box>
                            </Box>

                            <Box
                                role="group"
                                aria-label="App downloads"
                                sx={{
                                    width: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                    gap: { xs: 3, md: 5 },
                                }}
                            >
                                <Box
                                    sx={{
                                        gridColumn: { xs: '1', sm: '2' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Link
                                        href="https://apps.apple.com/hk/app/enspiren/id6448079759"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ display: 'block', lineHeight: 0 }}
                                    >
                                        <Box
                                            component="img"
                                            src={AppStoreLogo}
                                            alt="Download Enspiren on the App Store"
                                            sx={{ width: isMobile ? '200px' : '220px', height: 'auto', display: 'block' }}
                                        />
                                    </Link>
                                    <Link
                                        href="https://play.google.com/store/apps/details?id=com.kadima.demodraw&pcampaignid=web_share"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ display: 'block', lineHeight: 0 }}
                                    >
                                        <Box
                                            component="img"
                                            src={GooglePlayLogo}
                                            alt="Get Enspiren on Google Play"
                                            sx={{ width: isMobile ? '200px' : '220px', height: 'auto', display: 'block' }}
                                        />
                                    </Link>
                                </Box>

                                <Box
                                    sx={{
                                        gridColumn: { xs: '1', sm: '3' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Link href="https://apps.apple.com/hk/app/levav/id6754378820" sx={{ display: 'block', lineHeight: 0 }}>
                                        <Box
                                            component="img"
                                            src={AppStoreLogo}
                                            alt="AppStore"
                                            sx={{ width: isMobile ? '200px' : '220px', height: 'auto', display: 'block' }}
                                        />
                                    </Link>
                                    <Link href="https://play.google.com/store/apps/details?id=com.levav.app" sx={{ display: 'block', lineHeight: 0 }}>
                                        <Box
                                            component="img"
                                            src={GooglePlayLogo}
                                            alt="GooglePlay"
                                            sx={{ width: isMobile ? '200px' : '220px', height: 'auto', display: 'block' }}
                                        />
                                    </Link>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 1, md: 2 } }}>
                    <Box
                        component="ul"
                        sx={{
                            m: 0,
                            p: 0,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                            gap: { xs: 1.5, sm: 3 },
                            listStylePosition: 'inside',
                        }}
                    >
                        <Box component="li" sx={{ ...listItemStyle, mb: 0, textAlign: 'center' }}>
                            {t('ServiceFlow.card4_list3')}
                        </Box>
                        <Box component="li" sx={{ ...listItemStyle, mb: 0, textAlign: 'center' }}>
                            {t('ServiceFlow.card4_list4')}
                        </Box>
                        <Box component="li" sx={{ ...listItemStyle, mb: 0, textAlign: 'center' }}>
                            {t('ServiceFlow.card4_list5')}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
